<template>
  <div class="page-wrapper" style="font-family: Pretendard;">
    <div class="background-page">
      <div class="container purchase-container">
        <div v-if="state === 2" class="status-body">
          <div class="status-title">Loyalty card</div>
          <div class="status-text">{{message}}</div>
        </div>

        <div v-else-if="state === 1" class="status-body">
          <div class="status-title">Check your email</div>
          <div class="status-text">
            Your loyalty card has been sent to your email.
            Please check your inbox, and spam if you did not receive it.
          </div>
          <button class="primary-btn" @click="state = 0; email = ''">Close</button>
        </div>

        <div v-else-if="merchant" class="register-body">
          <div class="virtual-card">
            <div class="virtual-card-top">
              <div class="virtual-card-merchant-block">
                <div class="virtual-card-merchant">{{merchant.title}}</div>
                <div class="virtual-card-address" v-if="merchantAddress">{{merchantAddress}}</div>
              </div>
              <img v-if="merchant.logo" class="virtual-card-logo" :src="merchant.logo" alt=""/>
            </div>
            <div class="virtual-card-bottom">
              <div class="virtual-card-label">LOYALTY CARD</div>
              <div class="virtual-card-amount">Join</div>
              <div class="virtual-card-balance-label">Collect points when you spend</div>
            </div>
          </div>

          <div class="section-title">Register your loyalty card</div>
          <div class="section-copy">Enter your email to keep this loyalty card on your phone.</div>

          <div v-if="merchant.leaveLoyaltyPoint" class="closed-note">
            New membership is not available any more
          </div>

          <div class="form-block">
            Email <span class="mandatory-text">*</span>
            <input type="email" class="form-control" v-model="email" @keyup="correctEmail"
                   placeholder="email@example.com"/>
          </div>
          <div class="remember-row" @click="onRememberCheckboxClick">
            <span class="remember-check">
              <i v-if="remember" class="fas fa-check-square"></i>
              <i v-else class="far fa-square"></i>
            </span>
            <span>Remember this email and show my loyalty card automatically.</span>
          </div>
          <button class="primary-btn" @click="submit('qrcode')">Get Loyalty Card</button>

          <div class="group-logos" v-if="merchant.merchantList !== null && merchant.merchantList.length > 0">
            <img v-for="(m, mi) in merchant.merchantList" :key="'group-merchant-main-' + mi" :src="m.logo" alt=""/>
          </div>
        </div>

        <div class="page-footer">
          <div class="footer-merchant" v-if="merchant">
            <strong>{{merchant.title}}</strong>
            <div v-if="merchantAddress">{{merchantAddress}}</div>
          </div>
          <div class="privacy-note">
            Your email address is only used for this loyalty card and will not be shared with any third party.
          </div>
          <div class="tnc-html" v-if="merchant && merchant.tnc" v-html="merchant.tnc"></div>
          <div class="powered-by">
            Powered by <strong>Borium Studio Limited</strong>
          </div>
        </div>
      </div>
    </div>

    <Transition name="nested">
      <div class="sheet-overlay" v-if="loyaltyCardView">
        <div class="sheet-inner card-sheet-inner">
          <div class="card-sheet-header">
            <button type="button" class="back-link" @click="loyaltyCardView = false">‹ Back</button>
            <div class="card-sheet-title">Loyalty Card</div>
          </div>
          <div class="sheet-body card-sheet-body">
            <div class="virtual-card" v-if="merchant">
              <div class="virtual-card-top">
                <div class="virtual-card-merchant-block">
                  <div class="virtual-card-merchant">{{cardTitle}}</div>
                  <div class="virtual-card-address" v-if="merchantAddress">{{merchantAddress}}</div>
                </div>
                <img v-if="cardLogo" class="virtual-card-logo" :src="cardLogo" alt=""/>
              </div>
              <div class="virtual-card-bottom">
                <div class="virtual-card-label">LOYALTY CARD</div>
                <div class="virtual-card-amount" v-if="loyaltyCardView.customer">{{displayPoints}}</div>
                <div class="virtual-card-balance-label">Your points</div>
              </div>
            </div>

            <div class="qr-panel" v-if="loyaltyCardView.qrCode">
              <img :src="loyaltyCardView.qrCode" alt="Loyalty card QR code"/>
              <div class="qr-hint">Show this QR code at the till</div>
            </div>

            <a v-if="showApple && loyaltyCardView.customer" :href="walletUrl" class="wallet-btn">
              <i class="fab fa-apple"></i>
              Add to Apple Wallet
            </a>
            <a v-if="showGoogle && loyaltyCardView.customer" :href="googleWalletUrl" class="wallet-btn">
              <i class="fab fa-google"></i>
              Add to Google Wallet
            </a>
            <div class="registered-to" v-if="email">Registered to {{email}}</div>

            <div class="group-logos" v-if="merchant && merchant.merchantList !== null && merchant.merchantList.length > 0">
              <img v-for="(m, mi) in merchant.merchantList" :key="'group-merchant-' + mi" :src="m.logo" alt=""/>
            </div>

            <button v-if="merchant && (merchant.invitorPoint > 0 || merchant.inviteePoint > 0)"
                    class="primary-btn" type="button" @click="openReferForm">
              Referral reward
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="nested">
      <div class="sheet-overlay" v-if="newForm !== false" @click="newForm = false">
        <div class="sheet-inner" @click.stop="dummy">
          <div class="sheet-header">
            <div class="sheet-title">Create loyalty card</div>
            <button type="button" class="sheet-close" @click="newForm = false">×</button>
          </div>
          <div class="sheet-body">
            <div class="section-copy">
              <strong>No loyalty card</strong> found with your email address.
              Would you like to create a new loyalty card?
            </div>
            <div class="promo-note" v-if="merchant.pointRateForPromotionAgreed > merchant.pointRate">
              {{merchant.pointRateForPromotionAgreed}}% if you agree to receiving promotion emails
            </div>
            <div class="form-block">
              Email <span class="mandatory-text">*</span>
              <input v-model="newForm.email" type="email" class="form-control" placeholder="email@example.com"
                     @input="onSignupFormEmailEdited">
            </div>
            <div class="form-block">
              Name <span class="optional-text">(optional)</span>
              <input v-model="newForm.name" type="text" class="form-control" placeholder="Name">
            </div>
            <button class="primary-btn" @click="createLoyaltyCard">Create Loyalty Card</button>
            <div class="section-copy">Earn {{merchant.pointRate}}% on your spending.</div>
            <div class="remember-row" @click="newForm.allowMerchantPromotion = !newForm.allowMerchantPromotion">
              <span class="remember-check">
                <i v-if="newForm.allowMerchantPromotion" class="fas fa-check-square"></i>
                <i v-else class="far fa-square"></i>
              </span>
              <span><b>{{merchant.title}}</b> will send you offers or news by email unless you uncheck this box.</span>
            </div>
            <div class="group-logos" v-if="merchant.merchantList !== null && merchant.merchantList.length > 0">
              <img v-for="(m, mi) in merchant.merchantList" :key="'group-merchant-form-' + mi" :src="m.logo" alt=""/>
            </div>
            <div class="tnc-html" v-if="merchant.tnc" v-html="merchant.tnc"></div>
            <div class="footer-merchant">
              <strong>{{merchant.title}}</strong>
              <div v-if="merchant.address">{{merchant.address}}</div>
            </div>
            <div class="powered-by">
              Powered by <strong>Borium Studio Limited</strong>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="nested">
      <div class="dialog-overlay" v-if="reviewForm !== false">
        <div class="dialog-card">
          <div class="dialog-header">
            <div class="dialog-title">Google &amp; TripAdvisor Reviews</div>
            <button type="button" class="sheet-close dark" @click="reviewForm = false">×</button>
          </div>
          <div class="dialog-body">
            <div class="review-row" @click="openUrl(merchant.googleReviewUrl)">
              <img src="https://storage.googleapis.com/menus-275719.appspot.com/icons/ic_loyalty_google.png" alt=""/>
              <div>
                <div class="review-name">Google Review</div>
                <div class="review-copy">Google review approved and points added: {{merchant.reviewPoint}} points</div>
              </div>
            </div>
            <div class="review-row">
              <img src="https://storage.googleapis.com/menus-275719.appspot.com/icons/ic_loyalty_tripadvisor.png" alt=""/>
              <div>
                <div class="review-name">TripAdvisor Review</div>
                <div class="review-copy">TripAdvisor review awaiting approval!</div>
              </div>
            </div>
            <div class="form-block">
              Enter your name on the review
              <input class="form-control" v-model="reviewForm.name"/>
            </div>
            <button class="primary-btn" @click="submitReviewForm">Submit</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="nested">
      <div class="dialog-overlay" v-if="referForm !== false">
        <div class="dialog-card">
          <div class="dialog-header">
            <div class="dialog-title">Refer a friend</div>
            <button type="button" class="sheet-close dark" @click="referForm = false">×</button>
          </div>
          <div class="dialog-body">
            <div class="section-copy">
              When your friend joins by accepting your referral, your friend and you will get 100pts award point.
            </div>
            <div class="form-block">
              Your name
              <input class="form-control" v-model="referForm.invitorName"/>
            </div>
            <div class="form-block">
              Friend's name
              <input class="form-control" v-model="referForm.name"/>
            </div>
            <div class="form-block">
              Friend's email
              <input class="form-control" style="text-transform: lowercase;" v-model="referForm.email"/>
            </div>
            <button class="primary-btn" @click="submitReferForm">Send Referral</button>
          </div>
        </div>
      </div>
    </Transition>

    <Transition name="nested">
      <div class="dialog-overlay" v-if="dialog !== false">
        <div class="dialog-card">
          <div class="dialog-banner error" v-if="dialog.success === false">
            <img src="https://storage.googleapis.com/menus-275719.appspot.com/icons/smtm/sorry_cat.png" alt=""/>
          </div>
          <div class="dialog-header success" v-else>
            <div class="dialog-title light">{{dialog.title}}</div>
          </div>
          <div class="dialog-body center">
            {{dialog.description}}
            <button class="primary-btn" @click="dialog = false">Close</button>
          </div>
        </div>
      </div>
    </Transition>

    <div class="page-gif-loader" v-if="(!merchant && state !== 2) || checking">
      <UtensilLoader/>
    </div>
  </div>
</template>

<script>

import {
  getJoinLoyaltyCard,
  getLoyaltyCardEmail,
  getLoyaltyGoogleWalletUrl,
  getLoyaltyMerchant,
  getLoyaltyWalletUrl,
  sendLoyaltyInvitation,
  submitReviewRequest
} from "../api/borium";
import tnC from "./TnC.vue";
import {isPlatformHost} from "../api/hosts";
import UtensilLoader from "./UtensilLoader.vue";

export default {
  name:'PointRegister',
  components: { UtensilLoader },
  computed: {
    tnC() {
      return tnC
    },
    walletUrl: function() {
      if (!this.loyaltyCardView || !this.loyaltyCardView.customer) {
        return '';
      }
      return getLoyaltyWalletUrl(this.loyaltyCardView.customer.id);
    },
    googleWalletUrl: function() {
      if (!this.loyaltyCardView || !this.loyaltyCardView.customer) {
        return '';
      }
      return getLoyaltyGoogleWalletUrl(this.loyaltyCardView.customer.id);
    },
    showApple: function() {
      return this.isApple || !this.isGoogle;
    },
    showGoogle: function() {
      return this.isGoogle || !this.isApple;
    },
    isApple: function() {
      let ua = navigator.userAgent || '';
      return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
    },
    isGoogle: function() {
      return /Android/.test(navigator.userAgent || '');
    },
    merchantAddress: function() {
      if (!this.merchant) {
        return '';
      }
      let parts = [];
      if (this.merchant.address) {
        parts.push(String(this.merchant.address).replace(/\n/g, ', '));
      }
      if (this.merchant.city) parts.push(this.merchant.city);
      if (this.merchant.postCode) parts.push(this.merchant.postCode);
      return parts.join(', ');
    },
    cardTitle: function() {
      if (this.loyaltyCardView && this.loyaltyCardView.title) {
        return this.loyaltyCardView.title;
      }
      return this.merchant && this.merchant.title ? this.merchant.title : '';
    },
    cardLogo: function() {
      if (this.loyaltyCardView && this.loyaltyCardView.logo) {
        return this.loyaltyCardView.logo;
      }
      return this.merchant && this.merchant.logo ? this.merchant.logo : '';
    },
    displayPoints: function() {
      if (!this.loyaltyCardView || !this.loyaltyCardView.customer) {
        return '0';
      }
      let points = this.loyaltyCardView.customer.points;
      if (points === null || points === undefined) {
        return '0';
      }
      return Number(points).toLocaleString();
    }
  },
  watch: {
    email: function(newVal) {
      if(this.$data.emailHydrating) {
        return;
      }
      var n = this.normalizeLoyaltyEmail(newVal);
      if(n !== (this.$data.lastSyncedLoyaltyEmail || '')) {
        localStorage.removeItem('loyaltycard_email');
        this.$data.lastSyncedLoyaltyEmail = '';
      }
    }
  },
  data() {
    return {
      registrationClosedMsg: 'New membership is not available any more',
      email:'',
      state:0,
      message:'',
      loyaltyCardView:false,
      remember:true,
      merchantId:0,
      newForm:false,
      merchant: false,
      reviewForm: false,
      referForm: false,
      dialog:false,
      customDomain: false,
      checking: false,
      themeColour: '#E84725',
      lastSyncedLoyaltyEmail: '',
      emailHydrating: false,

    }
  },
  created() {
  },
  mounted() {
    this.initialise();
  },
  beforeDestroy() {
  },
  destroyed() {
  },
  methods: {

    dummy: function() {

    },
    initialise: function() {
      if(!isPlatformHost()) {
        this.$data.customDomain = window.location.host;
      }
      if(window.location.host !== '10.0.100.11'){
        this.$data.customDomain = 'app.k-points.co.uk';
      }
      if(this.$route.query.merchant !== undefined) {
        this.$data.merchantId = parseInt(this.$route.query.merchant);

      }
      this.getLoyaltyMerchant();

    },
    openUrl: function(url) {
      window.open(url, '_blank').focus();
    },
    getLoyaltyMerchant: function() {
      let self = this;
      if(this.$data.merchantId === 0 && this.$data.customDomain === false) {
        return;
      }
      getLoyaltyMerchant(this.$data.merchantId, this.$data.customDomain)
        .then(function(data) {
          if(data.success === true) {

            self.$data.merchant = data.item;
            self.$data.merchantId = data.item.merchantId;
            if(data.item.themeColour !== null && data.item.themeColour.length > 0) {
              self.$data.themeColour = '#' + data.item.themeColour;
            }
            self.onMerchantLoaded();
          }
        })
        .catch(function(err) {

        })
    },
    onMerchantLoaded: function() {
      let rememberEnabled = localStorage.getItem("loyaltycard_remember");
      if(rememberEnabled !== null) {

        this.$data.remember = rememberEnabled === 'true';
        if(this.$data.remember === true) {
          let storedEmail = localStorage.getItem("loyaltycard_email");
          if(storedEmail !== null && storedEmail.length > 0) {
            this.$data.emailHydrating = true;
            let norm = this.normalizeLoyaltyEmail(storedEmail);
            this.$data.lastSyncedLoyaltyEmail = norm;
            this.$data.email = norm;
            var selfMerch = this;
            this.$nextTick(function() {
              selfMerch.$data.emailHydrating = false;
            });
            this.submit('qrcode');
          } else {
            this.$data.lastSyncedLoyaltyEmail = '';
          }
        } else if(this.$data.remember === false) {
          this.$data.emailHydrating = true;
          this.$data.email = '';
          this.$data.lastSyncedLoyaltyEmail = '';
          localStorage.removeItem('loyaltycard_email');
          var selfForget = this;
          this.$nextTick(function() {
            selfForget.$data.emailHydrating = false;
          });

        }
      }
    },
    correctEmail: function() {
      if(this.$data.email !== null && this.$data.email.length > 0) {
        var norm = this.normalizeLoyaltyEmail(this.$data.email);
        if(norm !== this.$data.email) {
          this.$data.emailHydrating = true;
          this.$data.email = norm;
          var selfCorr = this;
          this.$nextTick(function() {
            selfCorr.$data.emailHydrating = false;
          });
        }
      }
    },
    normalizeLoyaltyEmail: function(val) {
      if(val === null || val === undefined) {
        return '';
      }
      return String(val).trim().toLowerCase();
    },
    onRememberCheckboxClick: function() {
      this.$data.remember = !this.$data.remember;
      localStorage.setItem('loyaltycard_remember', String(this.$data.remember));
    },
    onSignupFormEmailEdited: function() {
      localStorage.removeItem('loyaltycard_email');
      this.$data.lastSyncedLoyaltyEmail = '';
    },
    saveLoyaltyEmailToStorage: function() {
      this.$data.emailHydrating = true;
      var norm = this.normalizeLoyaltyEmail(this.$data.email);
      localStorage.setItem('loyaltycard_remember', String(this.$data.remember));
      if(norm.length > 0) {
        localStorage.setItem('loyaltycard_email', norm);
      } else {
        localStorage.removeItem('loyaltycard_email');
      }
      this.$data.lastSyncedLoyaltyEmail = norm;
      this.$data.email = norm;
      var selfSave = this;
      this.$nextTick(function() {
        selfSave.$data.emailHydrating = false;
      });
    },
    showRegistrationNotAllowedDialog: function(msg) {
      this.$data.dialog = {
        title:'Sorry',
        description: (msg && String(msg).length > 0) ? msg : this.$data.registrationClosedMsg,
        success: false,
      };
    },
    submit: function(type) {
      if(this.$data.email === null || this.$data.email.length === 0) {
        return;
      }

      let self = this;
      this.$data.checking = true;
      getLoyaltyCardEmail(this.$data.email, this.$data.merchantId, type)
        .then(function(data) {

          self.$data.checking = false;
          if(data.success === true) {
            self.saveLoyaltyEmailToStorage();

            if(type === 'qrcode') {
              self.$data.loyaltyCardView = data.item;
            } else {
              self.$data.state = 1;
            }

          } else {

            if(data.errorCode === 5003 || ((data.errorCode === 5001 || data.errorCode === 5002) && self.$data.merchant !== false && self.$data.merchant.leaveLoyaltyPoint)) {
              self.showRegistrationNotAllowedDialog(data.message);
              return;
            }
            if(data.errorCode === 5001 || data.errorCode === 5002) {
              self.showSubscribeView();
              return;
            }
            self.$data.message = data.message;
            self.$data.state = 2;
          }
        })
        .catch(function(err) {

          self.$data.checking = false;
          console.error('Request failed', err && err.response ? err.response.status : '');
        })
    },
    showSubscribeView: function() {
      if(this.$data.merchant !== false && this.$data.merchant.leaveLoyaltyPoint) {
        this.showRegistrationNotAllowedDialog();
        return;
      }
      this.$data.newForm = {
        email: this.$data.email,
        merchantId: this.$data.merchantId,
        // GDPR: marketing consent must be opt-in, so both default to unticked.
        allowSmtmPromotion: false,
        allowMerchantPromotion: false,
        name:'',
      }
    },
    createLoyaltyCard: function() {

      if(this.$data.merchant !== false && this.$data.merchant.leaveLoyaltyPoint) {
        this.showRegistrationNotAllowedDialog();
        return;
      }
      let self = this;
      this.$data.checking = true;
      getJoinLoyaltyCard(this.$data.newForm)
        .then(function(data) {

          self.$data.checking = false;
          if(data.success === true) {
            if(self.$data.newForm && typeof self.$data.newForm.email === 'string' && self.$data.newForm.email.length > 0) {
              self.$data.email = self.$data.newForm.email.trim().toLowerCase();
            }
            self.saveLoyaltyEmailToStorage();
            self.$data.newForm = false;
            self.$data.loyaltyCardView = data.item;
          } else {
            if(data.message === self.$data.registrationClosedMsg) {
              self.showRegistrationNotAllowedDialog(data.message);
            } else {
              console.error('Request failed');
            }
          }
        })
        .catch(function(err) {
          self.$data.checking = false;
          console.error('Request failed', err && err.response ? err.response.status : '');
        })
    },
    openReviewForm: function() {
      this.$data.reviewForm = {
        name: '',
        customerId:this.$data.loyaltyCardView.customer.id,
        merchantId: this.$data.merchant.id,
      }
    },
    submitReviewForm: function() {
      if(this.$data.reviewForm.name === null || this.$data.reviewForm.name.length < 3) {
        this.$data.dialog = {
          title:'Error',
          description:'Please enter your name on review',
          success:false,
        };

        return;
      }

      let self = this;
      submitReviewRequest(this.$data.reviewForm)
        .then(function(data) {
          if(data.success === true) {
            self.$data.dialog = {
              title:'Success',
              description:data.message,
              success: true,
            };
            self.$data.reviewForm = false;
          } else {
            self.$data.dialog = {
              title:'Sorry',
              description:data.message,
              success: false,
            };
          }
        })
        .catch(function(err) {
          console.error('Request failed', err && err.response ? err.response.status : '');

        })

    },
    openReferForm: function() {
      this.$data.referForm = {
        invitorId: this.$data.loyaltyCardView.customer.id,
        invitorName: this.$data.loyaltyCardView.customer.name,
        merchantId: this.$data.merchantId,
        name:'',
        email:'',
      }
    },
    submitReferForm: function() {
      if(this.$data.referForm.invitorName === null || this.$data.referForm.invitorName.length < 3) {
        this.$data.dialog = {
          title:'Error',
          description:'Please enter your name to help your friend recognise you',
          success:false,
        }
        return;
      }

      if(this.$data.referForm.email === null || this.$data.referForm.email.length < 3) {
        this.$data.dialog = {
          title:'Error',
          description:'Please enter the email address of your friend',
          success:false,
        }
        return;
      }
      if(this.$data.referForm.name === null || this.$data.referForm.name.length < 3) {
        this.$data.dialog = {
          title:'Error',
          description:'Please enter the name of your friend',
          success:false,
        }
        return;
      }
      let self = this;
      sendLoyaltyInvitation(this.$data.referForm)
        .then(function(data) {
          if(data.success === true) {
            self.$data.dialog = {
              title:'Success',
              description:data.message,
              success: true,
            };
            self.$data.referForm = false;
          } else {
            self.$data.dialog = {
              title:'Sorry',
              description:data.message,
              success: false,
            };
          }
        })
        .catch(function(err) {
          console.error('Request failed', err && err.response ? err.response.status : '');

        })
    }


  }
}

</script>
<style scoped>
.page-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  background: #f3f4f6;
  box-sizing: border-box;
}
.background-page {
  background: #f3f4f6;
  min-height: 100%;
}
.purchase-container.container,
.purchase-container {
  background: #f3f4f6;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding-bottom: 0;
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}
.register-body,
.status-body {
  padding: 20px 0 8px 0;
  text-align: left;
}
@media (min-width: 600px) {
  .register-body,
  .status-body {
    padding-left: 18px;
    padding-right: 18px;
  }
}
.status-title,
.page-kicker,
.section-title {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin-bottom: 14px;
}
.section-title {
  text-align: left;
  font-size: 20px;
  margin: 28px 0 8px 0;
}
.status-text,
.section-copy {
  font-size: 15px;
  color: #444;
  line-height: 1.5;
}
.section-copy {
  margin: 18px 0 8px 0;
}
.back-link {
  border: none;
  background: transparent;
  color: #031635;
  font-size: 16px;
  font-weight: 700;
  padding: 0 0 14px 0;
  cursor: pointer;
}
.virtual-card {
  background: linear-gradient(145deg, #0a2a52 0%, #031635 55%, #021024 100%);
  border-radius: 18px;
  min-height: 190px;
  padding: 22px 22px 20px 22px;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 8px 24px rgba(3, 22, 53, 0.28);
}
.virtual-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.virtual-card-bottom {
  margin-top: 20px;
}
.virtual-card-merchant-block {
  min-width: 0;
  padding-right: 12px;
}
.virtual-card-merchant {
  font-size: 22px;
  font-weight: 500;
  line-height: 1.2;
}
.virtual-card-address {
  margin-top: 10px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.35;
  opacity: 0.8;
}
.virtual-card-logo {
  width: 48px;
  height: 48px;
  object-fit: contain;
  background: #fff;
  border-radius: 50%;
  flex-shrink: 0;
}
.virtual-card-label {
  font-size: 12px;
  letter-spacing: 0.08em;
  opacity: 0.9;
  font-weight: 600;
}
.virtual-card-amount {
  font-size: 36px;
  font-weight: 600;
  line-height: 1.15;
  margin-top: 2px;
}
.virtual-card-balance-label {
  margin-top: 4px;
  font-size: 12px;
  letter-spacing: 0.04em;
  opacity: 0.8;
  font-weight: 500;
}
.qr-panel {
  margin-top: 18px;
  background: #fff;
  border-radius: 16px;
  padding: 18px 16px 16px 16px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
}
.qr-panel img {
  width: 200px;
  max-width: 70%;
  border-radius: 10px;
  background: #fff;
}
.qr-hint {
  margin-top: 10px;
  font-size: 13px;
  color: #555;
}
.form-block {
  text-align: left;
  padding: 8px 0;
  font-size: 14px;
  color: #333;
}
.form-block .form-control {
  margin-top: 6px;
  border-radius: 12px;
  min-height: 48px;
}
.mandatory-text {
  color: #E84725;
}
.optional-text {
  color: #888;
  font-size: 13px;
}
.primary-btn {
  margin-top: 16px;
  width: 100%;
  border: none;
  background: #031635;
  color: #fff;
  font-weight: 700;
  font-size: 16px;
  border-radius: 12px;
  padding: 16px 18px;
  cursor: pointer;
}
.wallet-btn {
  margin-top: 12px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #000;
  color: #fff;
  padding: 14px 28px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
}
.registered-to {
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  color: #555;
}
.remember-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 14px;
  font-size: 14px;
  color: #444;
  line-height: 1.45;
  cursor: pointer;
}
.remember-check {
  color: #031635;
  font-size: 22px;
  line-height: 1;
}
.closed-note {
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fdecea;
  color: #b71c1c;
  font-weight: 600;
  font-size: 14px;
}
.group-logos {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  padding: 18px 0 4px 0;
}
.group-logos img {
  height: 56px;
  width: auto;
}
.promo-note {
  margin-top: 8px;
  font-weight: 700;
  color: #031635;
}
.page-footer {
  margin-top: auto;
  padding: 28px 15px 36px 15px;
  border-top: 1px solid #eee;
  text-align: center;
}
.footer-merchant {
  margin-top: 0;
  font-size: 13px;
  color: #333;
  line-height: 1.45;
}
.privacy-note,
.powered-by {
  margin-top: 14px;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
.powered-by {
  margin-top: 12px;
}
.tnc-html {
  margin-top: 16px;
  font-size: 12px;
  color: #666;
  text-align: left;
  line-height: 1.5;
}
.sheet-overlay,
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.3);
  z-index: 20;
}
.dialog-overlay {
  background: rgba(0,0,0,0.45);
  z-index: 30;
}
.sheet-inner {
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 95%;
  background: #f3f4f6;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.card-sheet-inner {
  height: 100%;
  border-radius: 0;
}
.card-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 60px;
  padding: 8px 18px;
  background: #fff;
  flex-shrink: 0;
}
.card-sheet-header .back-link {
  padding: 0;
}
.card-sheet-title {
  font-size: 18px;
  font-weight: 700;
  color: #111;
}
.card-sheet-body {
  height: 100%;
  padding-top: 12px;
}
.sheet-header,
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: #031635;
  color: #fff;
}
.sheet-title,
.dialog-title {
  font-size: 18px;
  font-weight: 700;
}
.dialog-title.light {
  color: #fff;
}
.sheet-close {
  border: none;
  background: transparent;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
}
.sheet-close.dark {
  color: #fff;
}
.sheet-body,
.dialog-body {
  padding: 16px 18px 28px 18px;
  overflow-y: auto;
  text-align: left;
}
.dialog-card {
  position: absolute;
  top: 50%;
  left: 50%;
  width: calc(100% - 50px);
  max-width: 400px;
  max-height: 80%;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  transform: translate(-50%, -50%);
}
.dialog-body.center {
  text-align: center;
}
.dialog-header.success {
  min-height: 72px;
}
.dialog-banner.error {
  height: 220px;
  background: #f3f4f6;
  text-align: center;
}
.dialog-banner.error img {
  height: 100%;
}
.review-row {
  display: flex;
  gap: 14px;
  text-align: left;
  margin-bottom: 18px;
  cursor: pointer;
}
.review-row img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
.review-name {
  font-weight: 700;
  font-size: 16px;
  text-decoration: underline;
  color: #031635;
}
.review-copy {
  margin-top: 6px;
  font-size: 13px;
  color: #555;
  line-height: 1.4;
}
.nested-enter-active, .nested-leave-active {
  transition: all 0.3s ease-in-out;
}
.nested-enter,
.nested-leave-to {
  opacity: 0 !important;
}
.nested-enter-active .sheet-inner,
.nested-leave-active .sheet-inner,
.nested-enter-active .dialog-card,
.nested-leave-active .dialog-card {
  transition: transform 0.3s ease-in-out;
}
.nested-enter .sheet-inner,
.nested-leave-to .sheet-inner {
  transform: translateY(100%);
}
</style>



// WEBPACK FOOTER //
// src/components/PointRegister.vue